import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchMatterRequestTypes } from '../../hooks/useFetchMatterRequestTypes';
import { IMatterType } from '../../interface/matter';
import { IMatterRequest } from '../../interface/matterRequest';

type IdentifiedMatterType = IMatterType & { id: string };

/** The props `SearchableSelect` needs to pick a matter request's type, from the same list the picker screen uses. */
export const useMatterRequestTypeSelect = () => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { language: appLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: matterRequestTypes,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchMatterRequestTypes({ pageSize: 50, search: searchValue }, true);

  const toOption = ({ id, translations }: IdentifiedMatterType): SelectOption => ({
    value: id,
    label: getTranslationFromTranslatableEntity(translations, appLanguage),
  });

  const options = matterRequestTypes
    .map(toOption)
    .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label, appLanguage));
  const value =
    matterRequest?.matterRequestTypeId && matterRequest.matterRequestType
      ? toOption({ ...matterRequest.matterRequestType, id: matterRequest.matterRequestTypeId })
      : null;

  const onChange = (option: SelectOption) => {
    const matterRequestType = matterRequestTypes.find(({ id }: IMatterType) => id === option.value);
    if (!matterRequestType) return;
    updateManageMatterRequest({
      ...matterRequest,
      matterRequestTypeId: matterRequestType.id,
      matterRequestType,
      isMatterTypeSetByUser: true,
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
